import type { AnimName, Direction } from "@agency/shared";
import type Phaser from "phaser";
import { animKey } from "../anims.js";
import { worldTextStyle } from "../text.js";

const PIP_W = 18;
const PIP_H = 3;
/** Sprite frames are anchored at their feet; the head sits this far above. */
const SPRITE_TOP = -24;
/** Vertical gap between stacked overhead labels. */
const LABEL_GAP = 2;
/**
 * Overhead labels render above all world objects (desks, chairs) but below
 * the interaction prompt; adding y keeps nearer avatars' labels on top.
 */
const LABEL_DEPTH = 100_000;

/** Visual for a single avatar: sprite, name tag, exhaustion pip bar, bubble. */
export class AvatarView {
  readonly container: Phaser.GameObjects.Container;
  readonly sprite: Phaser.GameObjects.Sprite;
  /** Name tag, pip bar, bubble etc. — separate so furniture can't cover them. */
  private readonly labels: Phaser.GameObjects.Container;

  private readonly nameTag: Phaser.GameObjects.Text;
  private readonly pipBg: Phaser.GameObjects.Rectangle;
  private readonly pipFg: Phaser.GameObjects.Rectangle;
  private readonly bubble: Phaser.GameObjects.Text;
  private readonly emote: Phaser.GameObjects.Text;
  private readonly zzz: Phaser.GameObjects.Text;

  private readonly avatarId: string;
  private dir: Direction = 0;
  private moving = false;
  private activity = "idle";
  private bubbleUntil = 0;
  private emoteUntil = 0;
  /** Top of the overhead label stack, relative to the avatar's feet. */
  private stackTop = SPRITE_TOP;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    avatarId: string,
    nickname: string,
    isLocal: boolean,
  ) {
    this.avatarId = avatarId;
    this.sprite = scene.add.sprite(0, 0, avatarId);
    this.sprite.setOrigin(0.5, 1);

    this.nameTag = scene.add
      .text(
        0,
        -34,
        nickname,
        worldTextStyle(scene, {
          fontSize: "8px",
          color: isLocal ? "#ffe08a" : "#e8e8f0",
          backgroundColor: "#00000066",
          padding: { x: 2, y: 1 },
        }),
      )
      .setOrigin(0.5, 1);

    this.pipBg = scene.add
      .rectangle(-PIP_W / 2, -30, PIP_W, PIP_H, 0x000000, 0.6)
      .setOrigin(0, 0.5);
    this.pipFg = scene.add.rectangle(-PIP_W / 2, -30, PIP_W, PIP_H, 0x4caf50).setOrigin(0, 0.5);

    this.bubble = scene.add
      .text(
        0,
        -46,
        "",
        worldTextStyle(scene, {
          fontSize: "8px",
          color: "#12141c",
          backgroundColor: "#ffffff",
          padding: { x: 3, y: 2 },
          align: "center",
          wordWrap: { width: 120 },
        }),
      )
      .setOrigin(0.5, 1)
      .setVisible(false);

    this.emote = scene.add
      .text(0, -60, "", worldTextStyle(scene, { fontSize: "14px" }))
      .setOrigin(0.5, 1)
      .setVisible(false);

    this.zzz = scene.add
      .text(0, 0, "zZz", worldTextStyle(scene, { fontSize: "8px", color: "#cfd3ff" }))
      .setOrigin(0, 1)
      .setVisible(false);

    this.container = scene.add.container(x, y, [this.sprite]);
    this.container.setDepth(y);
    this.labels = scene.add.container(x, y, [
      this.pipBg,
      this.pipFg,
      this.nameTag,
      this.bubble,
      this.emote,
      this.zzz,
    ]);
    this.labels.setDepth(LABEL_DEPTH + y);
    this.layout();
    this.play();
  }

  setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
    this.container.setDepth(y);
    this.labels.setPosition(x, y);
    this.labels.setDepth(LABEL_DEPTH + y);
  }

  setName(nickname: string): void {
    if (this.nameTag.text === nickname) return;
    this.nameTag.setText(nickname);
    this.layout();
  }

  /** World y of the topmost visible overhead label, for stacking prompts above it. */
  labelTop(): number {
    return this.container.y + this.stackTop;
  }

  setDirection(dir: Direction, moving: boolean): void {
    this.dir = dir;
    this.moving = moving;
    this.play();
  }

  setActivity(activity: string): void {
    this.activity = activity;
    this.play();
  }

  setExhaustion(pct: number): void {
    const clamped = Math.max(0, Math.min(100, pct));
    this.pipFg.displayWidth = (PIP_W * clamped) / 100;
    const color = clamped >= 80 ? 0xe53935 : clamped >= 50 ? 0xfdd835 : 0x4caf50;
    this.pipFg.setFillStyle(color);
    this.pipFg.setVisible(clamped > 0.5);
  }

  setConnected(connected: boolean): void {
    this.sprite.setAlpha(connected ? 1 : 0.55);
    this.zzz.setVisible(!connected);
  }

  showBubble(text: string, until: number): void {
    this.bubble.setText(text);
    this.bubble.setVisible(true);
    this.bubbleUntil = until;
    this.layout();
  }

  showEmote(emoji: string, until: number): void {
    this.emote.setText(emoji);
    this.emote.setVisible(true);
    this.emoteUntil = until;
    this.layout();
  }

  update(now: number): void {
    let changed = false;
    if (this.bubbleUntil && now > this.bubbleUntil) {
      this.bubble.setVisible(false);
      this.bubbleUntil = 0;
      changed = true;
    }
    if (this.emoteUntil && now > this.emoteUntil) {
      this.emote.setVisible(false);
      this.emoteUntil = 0;
      changed = true;
    }
    if (changed) this.layout();
  }

  /**
   * Stack the overhead labels bottom-up (pip bar, name tag, chat bubble, emote)
   * using their measured heights, so multi-line bubbles never overlap the name.
   */
  private layout(): void {
    let y = SPRITE_TOP - LABEL_GAP;
    this.pipBg.setY(y - PIP_H / 2);
    this.pipFg.setY(y - PIP_H / 2);
    y -= PIP_H + LABEL_GAP;

    this.nameTag.setY(y);
    this.zzz.setPosition(this.nameTag.width / 2 + LABEL_GAP, y);
    y -= this.nameTag.height + LABEL_GAP;

    if (this.bubble.visible) {
      this.bubble.setY(y);
      y -= this.bubble.height + LABEL_GAP;
    }
    if (this.emote.visible) {
      this.emote.setY(y);
      y -= this.emote.height + LABEL_GAP;
    }
    this.stackTop = y + LABEL_GAP;
  }

  destroy(): void {
    this.container.destroy(true);
    this.labels.destroy(true);
  }

  private play(): void {
    const anim: AnimName =
      this.activity === "working"
        ? "sit"
        : this.activity === "coffee"
          ? "drink"
          : this.animForMove();
    const key = animKey(this.avatarId, anim);
    if (this.sprite.anims.currentAnim?.key !== key) this.sprite.play(key);
  }

  private animForMove(): AnimName {
    const name = (["down", "left", "right", "up"] as const)[this.dir] ?? "down";
    return `${this.moving ? "walk" : "idle"}_${name}` as AnimName;
  }
}
